import {
    Box,
    IconButton,
    InputBaseComponentProps,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { ChangeEvent, ReactNode, useState } from 'react';

interface NumberInputProps {
    value: number | null;
    onChange: (value: number | null) => void;
    step?: number;
    min?: number;
    max?: number;
    precision?: number; // New prop
    placeholder?: string;
    disabled?: boolean;
    inputProps?: InputBaseComponentProps;
    fullWidth?: boolean;
    label?: ReactNode;
    autoFocus?: boolean;
    error?: string | null;
}

const roundToPrecision = (num: number, precision: number = 10) => {
    const factor = Math.pow(10, precision);
    return Math.round(num * factor) / factor;
};

const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    step = 1,
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    precision = 2,
    placeholder = '',
    disabled = false,
    inputProps,
    label,
    fullWidth = false,
    autoFocus = false,
    error,
}) => {
    const [internalValue, setInternalValue] = useState<string>(
        value === null || value === undefined ? '' : value.toFixed(precision)
    );
    const [isTouched, setIsTouched] = useState(false);

    const validateValue = (val: string): boolean => {
        const num = parseFloat(val);
        return !isNaN(num) && num >= min && num <= max;
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        if (
            newValue === '' ||
            new RegExp(`^-?\\d*\\.?\\d{0,${precision}}$`).test(newValue)
        ) {
            setInternalValue(newValue);
            setIsTouched(true);
        }
    };

    const handleIncrement = () => {
        if (disabled) return;
        const newValue =
            value !== null
                ? Math.min(roundToPrecision(value + step, precision), max)
                : roundToPrecision(step, precision);
        setInternalValue(newValue.toFixed(precision));
        onChange(newValue);
        setIsTouched(false);
    };

    const handleDecrement = () => {
        if (disabled) return;
        const newValue =
            value !== null
                ? Math.max(roundToPrecision(value - step, precision), min)
                : roundToPrecision(-step, precision);
        setInternalValue(newValue.toFixed(precision));
        onChange(newValue);
        setIsTouched(false);
    };

    const handleBlur = () => {
        if (!isTouched) return;

        const numericValue = parseFloat(internalValue);
        if (validateValue(internalValue)) {
            const clampedValue = Math.max(
                Math.min(roundToPrecision(numericValue, precision), max),
                min
            );
            setInternalValue(clampedValue.toFixed(precision));
            onChange(clampedValue);
        } else {
            setInternalValue(value !== null ? value.toFixed(precision) : '');
        }
        setIsTouched(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
            handleBlur();
        }
    };

    const tooltipContent = () => {
        const parts: string[] = [];
        if (min > Number.NEGATIVE_INFINITY) parts.push(`Min: ${min}`);
        if (max < Number.POSITIVE_INFINITY) parts.push(`Max: ${max}`);
        return parts.join(', ');
    };

    return (
        <Tooltip
            open={isTouched}
            placement="bottom-start"
            title={tooltipContent() || ''}
            disableHoverListener={!tooltipContent()}
            PopperProps={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, -12],
                        },
                    },
                ],
            }}
        >
            <Box position="relative">
                <TextField
                    variant="outlined"
                    size="small"
                    fullWidth={fullWidth}
                    value={internalValue}
                    label={label}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    disabled={disabled}
                    error={Boolean(
                        error || (!validateValue(internalValue) && isTouched)
                    )}
                    inputProps={{
                        inputMode: 'decimal',
                        ...inputProps,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            paddingRight: '16px',
                            paddingLeft: '8px',
                        },
                        '& .MuiInputBase-input': {
                            paddingRight: '16px',
                            paddingLeft: '8px',
                        },
                    }}
                />
                <Box display="grid" position="absolute" top={4} right={4}>
                    <IconButton
                        size="small"
                        onClick={handleIncrement}
                        disabled={disabled}
                        sx={{
                            fontSize: 8,
                            padding: '1px',
                        }}
                    >
                        &#9650;
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={handleDecrement}
                        disabled={disabled}
                        sx={{
                            fontSize: 8,
                            padding: '1px',
                        }}
                    >
                        &#9660;
                    </IconButton>
                </Box>
                {(error || (!validateValue(internalValue) && isTouched)) && (
                    <Typography
                        fontSize={10}
                        variant="body2"
                        color="error"
                        mt={0.1}
                    >
                        {error || 'Invalid value'}
                    </Typography>
                )}
            </Box>
        </Tooltip>
    );
};

export default NumberInput;
